import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, Minus, Target, Brain, MessageSquare } from "lucide-react";
import { ProgressMetrics } from "@/hooks/useProgress";

interface ProgressChartProps {
  progress: ProgressMetrics;
}

export const ProgressChart = ({ progress }: ProgressChartProps) => {
  const { recentScores, metrics, improvement, averageScore, totalConversations } = progress;

  // Get max score for chart scaling
  const maxScore = Math.max(...recentScores.map(s => s.overall_score), 100);
  
  const getTrendIcon = () => {
    if (improvement > 5) return <TrendingUp className="h-4 w-4 text-success" />;
    if (improvement < -5) return <TrendingDown className="h-4 w-4 text-destructive" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  const getTrendColor = () => {
    if (improvement > 5) return 'text-success';
    if (improvement < -5) return 'text-destructive';
    return 'text-muted-foreground';
  };

  if (recentScores.length === 0 && totalConversations === 0) {
    return (
      <Card className="card-hello border-0">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            📈 Seu Progresso
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 space-y-3">
            <div className="text-4xl">🎯</div>
            <p className="text-muted-foreground">
              Complete algumas conversas para ver seu progresso aqui!
            </p>
            <p className="text-sm text-muted-foreground">
              Vamos acompanhar sua fluência, vocabulário e confiança.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-hello border-0">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            📈 Seu Progresso
          </CardTitle>
          <div className="flex items-center gap-2">
            {getTrendIcon()}
            <span className={`text-sm font-medium ${getTrendColor()}`}>
              {improvement > 0 ? '+' : ''}{improvement}%
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Score Overview */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-3xl font-bold text-primary">{averageScore}</p>
            <p className="text-sm text-muted-foreground">Pontuação média</p>
          </div>
          <Badge variant="secondary" className="text-sm">
            {totalConversations} conversa{totalConversations !== 1 ? 's' : ''}
          </Badge>
        </div>

        {recentScores.length > 0 ? (
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">Últimas conversas</p>
          <div className="flex items-end gap-1 h-24">
            {recentScores.map((score, index) => (
              <div
                key={score.id}
                className="flex-1 bg-primary/20 rounded-t-sm relative group cursor-pointer transition-all hover:bg-primary/30"
                style={{ height: `${(score.overall_score / maxScore) * 100}%` }}
              >
                <div
                  className="absolute bottom-0 left-0 right-0 bg-primary rounded-t-sm transition-all"
                  style={{ height: `${(score.overall_score / maxScore) * 100}%` }}
                />
                {/* Tooltip */}
                <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-foreground text-background text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  {score.overall_score} pts
                </div>
              </div>
            ))}
          </div>
        </div>
        ) : null}

        {/* Metrics Breakdown */}
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 rounded-xl bg-accent/10">
            <MessageSquare className="h-5 w-5 mx-auto mb-1 text-primary" />
            <p className="text-lg font-bold">{metrics.fluency}</p>
            <p className="text-xs text-muted-foreground">Fluência</p>
          </div>
          <div className="text-center p-3 rounded-xl bg-accent/10">
            <Brain className="h-5 w-5 mx-auto mb-1 text-secondary" />
            <p className="text-lg font-bold">{metrics.vocabulary}</p>
            <p className="text-xs text-muted-foreground">Vocabulário</p>
          </div>
          <div className="text-center p-3 rounded-xl bg-accent/10">
            <Target className="h-5 w-5 mx-auto mb-1 text-accent" />
            <p className="text-lg font-bold">{metrics.confidence}</p>
            <p className="text-xs text-muted-foreground">Confiança</p>
          </div>
        </div>

        {/* Progress Tips */}
        <div className="text-sm text-muted-foreground bg-muted/30 p-3 rounded-xl">
          {improvement > 10 && (
            <p>🔥 <strong>Incrível!</strong> Você está evoluindo rápido. Continue assim!</p>
          )}
          {improvement > 0 && improvement <= 10 && (
            <p>📈 <strong>Bom progresso!</strong> Cada conversa te deixa mais fluente.</p>
          )}
          {improvement === 0 && (
            <p>💪 <strong>Consistência é chave!</strong> Continue praticando diariamente.</p>
          )}
          {improvement < 0 && (
            <p>🌱 <strong>Tudo bem!</strong> O aprendizado não é linear. O importante é continuar!</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
